import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosRDV } from "../api/axios";

export const MEDICAL_ACCESS_SCOPE_OPTIONS = [
  { value: "medical_information:read", label: "Lire les informations médicales" },
  { value: "medical_information:create", label: "Ajouter des informations médicales" },
  { value: "medical_information:update", label: "Modifier les informations médicales" },
  { value: "medical_document:read", label: "Lire les documents médicaux" },
  { value: "medical_document:create", label: "Ajouter des documents médicaux" },
  { value: "medication:read", label: "Lire les traitements" },
  { value: "medication:create", label: "Créer un traitement" },
  { value: "medication:update", label: "Modifier les traitements" },
  { value: "medication:stop", label: "Arrêter les traitements" },
];

export const DEFAULT_ACCESS_SCOPES = MEDICAL_ACCESS_SCOPE_OPTIONS.map(
  ({ value }) => value,
);

const emptyCapabilities = () => ({
  medicalInformation: { read: false, create: false, update: false, delete: false },
  medicalDocuments: {
    read: false,
    create: false,
    update: false,
    archive: false,
    delete: false,
  },
  medications: {
    read: false,
    create: false,
    update: false,
    stop: false,
    delete: false,
    recordIntake: false,
  },
});

const CACHE_TTL_MS = 30_000;
const capabilityCache = new Map();

const keyFor = (doctorId, patientId) =>
  `${String(doctorId || "").trim()}:${String(patientId || "").trim()}`;

export const invalidateMedicalCapabilities = (patientId, doctorId, extraPatientIds = []) => {
  const patientCandidates = Array.from(
    new Set(
      [patientId, ...extraPatientIds]
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );
  const doctor = String(doctorId || "").trim();
  for (const key of capabilityCache.keys()) {
    const matchesPatient =
      patientCandidates.length === 0 ||
      patientCandidates.some((patient) => key.endsWith(`:${patient}`));
    const matchesDoctor = !doctor || key.startsWith(`${doctor}:`);
    if (matchesPatient && matchesDoctor) {
      capabilityCache.delete(key);
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("medical-capabilities-invalidated", {
        detail: {
          patientId: patientCandidates[0] || null,
          patientIds: patientCandidates,
          doctorId: doctor || null,
        },
      }),
    );
  }
};

export default function useMedicalCapabilities(patientId, doctorId, subprofileId = null) {
  const normalizedSubprofileId = String(subprofileId || "").trim() || null;
  const cachePatientId = normalizedSubprofileId || patientId;
  const key = keyFor(doctorId, cachePatientId);
  const [state, setState] = useState({
    loading: Boolean(patientId && doctorId),
    status: "none",
    validUntil: null,
    grantId: null,
    capabilities: emptyCapabilities(),
    error: null,
  });

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!patientId || !doctorId) {
        setState((current) => ({
          ...current,
          loading: false,
          status: "missing_identity",
          capabilities: emptyCapabilities(),
        }));
        return;
      }

      const cached = capabilityCache.get(key);
      if (!force && cached && cached.expiresAt > Date.now()) {
        setState({ ...cached.value, loading: false, error: null });
        return;
      }

      setState((current) => ({
        ...current,
        loading: force ? current.loading : true,
        error: null,
      }));
      try {
        const { data } = await axiosRDV.get("/access-requests/capabilities", {
          params: {
            doctorId,
            patientId,
            ...(normalizedSubprofileId
              ? { subprofileId: normalizedSubprofileId }
              : {}),
          },
        });
        const value = {
          loading: false,
          status: data?.status || "none",
          validUntil: data?.validUntil || null,
          grantId: data?.grantId || null,
          capabilities: data?.capabilities || emptyCapabilities(),
          error: null,
        };
        capabilityCache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
        setState(value);
      } catch (error) {
        setState({
          loading: false,
          status: error?.response?.status === 403 ? "revoked_or_denied" : "unavailable",
          validUntil: null,
          grantId: null,
          capabilities: emptyCapabilities(),
          error,
        });
      }
    },
    [doctorId, key, normalizedSubprofileId, patientId],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (state.status !== "pending") return undefined;
    const intervalId = setInterval(() => load({ force: true }), 15000);
    return () => clearInterval(intervalId);
  }, [load, state.status]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onInvalidated = (event) => {
      const detail = event?.detail || {};
      const targetPatients = Array.from(
        new Set(
          [detail.patientId, ...(detail.patientIds || [])]
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ),
      );
      const targetDoctor = String(detail.doctorId || "").trim();
      const currentProfiles = [patientId, normalizedSubprofileId]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      const currentDoctor = String(doctorId || "").trim();

      const patientMatches =
        targetPatients.length === 0 ||
        targetPatients.some((candidate) => currentProfiles.includes(candidate));
      const doctorMatches = !targetDoctor || targetDoctor === currentDoctor;

      if (patientMatches && doctorMatches) {
        capabilityCache.delete(key);
        load({ force: true });
      }
    };
    window.addEventListener("medical-capabilities-invalidated", onInvalidated);
    return () => window.removeEventListener("medical-capabilities-invalidated", onInvalidated);
  }, [doctorId, key, load, normalizedSubprofileId, patientId]);

  return useMemo(
    () => ({ ...state, refresh: () => load({ force: true }) }),
    [load, state],
  );
}
